/**
 * Communications (inbox) service, REST over `/planning/:project_id/inbox*`.
 *
 * Source of truth is the API. An in-memory cache holds the last snapshot for
 * the active project and a change event tells mounted views to re-read it.
 * Nothing is written to localStorage, and no provider secret is ever kept here:
 * the Gupshup details go to the API once and only the masked summary comes back.
 */
import { apiDelete, apiGet, apiPost, apiPut, planningPath } from './PlanningApiClient';
import { isApiError } from '../utils/apiErrors';
import { getActiveProjectId } from '../utils/session';
import type {
  GupshupKeysInput,
  InboxMessage,
  InboxSnapshot,
  MessageContact,
  MessagingKeySummary,
  NoteInput,
  ProviderStatus,
  SendMessageInput,
} from '../types/Communications';

export const COMMUNICATIONS_CHANGE_EVENT = 'ans_communications_change';

export const PROVIDER_NOT_CONFIGURED_MESSAGE =
  'WhatsApp is not set up for this project yet. Ask an admin to add the Gupshup details.';
const RECIPIENT_UNRESOLVABLE_MESSAGE =
  'We could not find a phone number for that contact. Pick another contact or type a number.';
const PHONE_REGION_MISSING_MESSAGE =
  'This project has no phone region set, so that number cannot be read. Type it in international form, +<country code><number>, or set the region in project settings.';

/** An error from this service whose `message` is safe to show as is. */
export class CommunicationsError extends Error {
  code: string | null;
  status: number | null;
  /** On a 502 the API returns the stored message with `delivery_error` set. */
  failedMessage: InboxMessage | null;

  constructor(message: string, opts: { code?: string | null; status?: number | null; failedMessage?: InboxMessage | null } = {}) {
    super(message);
    this.name = 'CommunicationsError';
    this.code = opts.code ?? null;
    this.status = opts.status ?? null;
    this.failedMessage = opts.failedMessage ?? null;
  }

  get isProviderNotConfigured(): boolean {
    return this.code === 'provider_not_configured';
  }
}

// ── cache ───────────────────────────────────────────────────────────────────

let cache: InboxSnapshot = { messages: [], mutedThreads: [] };
let cacheProject: string | null = null;

function setCache(next: InboxSnapshot): void {
  cache = next;
  cacheProject = getActiveProjectId();
  window.dispatchEvent(new Event(COMMUNICATIONS_CHANGE_EVENT));
}

function cacheIsCurrent(): boolean {
  return cacheProject === getActiveProjectId();
}

/** Messages for the active project (empty until loaded, or after a project switch). */
export function getCommunicationsMessages(): InboxMessage[] {
  return cacheIsCurrent() ? cache.messages : [];
}

export function getMutedThreads(): string[] {
  return cacheIsCurrent() ? cache.mutedThreads : [];
}

export function resetCommunicationsCache(): void {
  cache = { messages: [], mutedThreads: [] };
  cacheProject = null;
}

// ── mapping ─────────────────────────────────────────────────────────────────

type Raw = Partial<Record<keyof InboxMessage, unknown>>;

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function toInboxMessage(row: Raw): InboxMessage {
  const priority = row.priority === 'urgent' ? 'urgent' : 'normal';
  return {
    id: String(row.id ?? ''),
    type: str(row.type) ?? 'note',
    recipient: str(row.recipient),
    subject: typeof row.subject === 'string' ? row.subject : '',
    body: typeof row.body === 'string' ? row.body : '',
    priority,
    followup_date: str(row.followup_date),
    read: row.read === true,
    status: (str(row.status) as InboxMessage['status']) ?? null,
    source: (str(row.source) as InboxMessage['source']) ?? null,
    jid: str(row.jid),
    wa_sender: str(row.wa_sender),
    direction: (str(row.direction) as InboxMessage['direction']) ?? null,
    channel_provider: str(row.channel_provider),
    delivery_status: (str(row.delivery_status) as InboxMessage['delivery_status']) ?? null,
    delivery_error: str(row.delivery_error),
    error_reason: str(row.error_reason),
    created_by: str(row.created_by),
    created_at: typeof row.created_at === 'string' ? row.created_at : '',
    updated_at: str(row.updated_at),
    thread_key: str(row.thread_key),
    muted: row.muted === true,
    contact: toContact(row.contact),
  };
}

function toContact(v: unknown): InboxMessage['contact'] {
  if (!v || typeof v !== 'object') return null;
  const c = v as Record<string, unknown>;
  if (c.type !== 'guest' || c.id == null) return null;
  return { type: 'guest', id: String(c.id), name: typeof c.name === 'string' ? c.name : '' };
}

/** Maps any thrown API error to one whose message is plain and safe to show. */
export function mapCommunicationsError(err: unknown, fallback = 'That did not go through. Please try again.'): CommunicationsError {
  if (err instanceof CommunicationsError) return err;
  if (!isApiError(err)) {
    return new CommunicationsError(err instanceof Error && err.message ? err.message : fallback);
  }
  const { status, code } = err;
  if (code === 'provider_not_configured') {
    return new CommunicationsError(PROVIDER_NOT_CONFIGURED_MESSAGE, { code, status });
  }
  if (code === 'recipient_unresolvable') {
    return new CommunicationsError(RECIPIENT_UNRESOLVABLE_MESSAGE, { code, status });
  }
  if (code === 'phone_region_missing') {
    return new CommunicationsError(PHONE_REGION_MISSING_MESSAGE, { code, status });
  }
  if (status === 502) {
    const failed = failedMessageFrom(err.body);
    const reason = failed?.delivery_error ?? failed?.error_reason;
    const text = reason
      ? `The message was not delivered: ${reason}`
      : 'The message was not delivered. The WhatsApp provider rejected it.';
    return new CommunicationsError(text, { code: code ?? 'delivery_failed', status, failedMessage: failed });
  }
  return new CommunicationsError(err.message, { code, status });
}

function failedMessageFrom(body: unknown): InboxMessage | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const candidate = b.id != null ? b : (b.message && typeof b.message === 'object' ? (b.message as Raw) : null);
  return candidate ? toInboxMessage(candidate as Raw) : null;
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw mapCommunicationsError(err);
  }
}

function upsert(messages: InboxMessage[], msg: InboxMessage): InboxMessage[] {
  return messages.some(m => m.id === msg.id)
    ? messages.map(m => (m.id === msg.id ? msg : m))
    : [msg, ...messages];
}

// ── inbox ───────────────────────────────────────────────────────────────────

/**
 * Loads the inbox for the active project into the cache. On failure the last
 * snapshot stays in place and the mapped error is thrown so the view can show it.
 */
export async function refreshInboxFromApi(): Promise<InboxSnapshot> {
  const requestedFor = getActiveProjectId();
  const res = await call(() =>
    apiGet<{ messages?: Raw[]; muted_threads?: string[] }>(planningPath('/inbox')),
  );
  // The person switched project while this was in flight: do not file one
  // project's messages under another.
  if (getActiveProjectId() !== requestedFor) return { messages: [], mutedThreads: [] };
  const snapshot: InboxSnapshot = {
    messages: (res?.messages ?? []).map(toInboxMessage),
    mutedThreads: Array.isArray(res?.muted_threads) ? res.muted_threads : [],
  };
  setCache(snapshot);
  return snapshot;
}

export async function loadProviderStatus(): Promise<ProviderStatus> {
  const res = await call(() =>
    apiGet<{
      provider?: 'gupshup'; configured?: boolean; can_send?: boolean;
      reason?: ProviderStatus['reason']; masked?: string | null; webhook_path?: string;
    }>(planningPath('/inbox/provider')),
  );
  return {
    provider: 'gupshup',
    configured: res?.configured === true,
    canSend: res?.can_send === true,
    reason: res?.reason ?? null,
    masked: res?.masked ?? null,
    webhookPath: res?.webhook_path || '/webhooks/gupshup',
  };
}

export async function searchContacts(search: string, limit = 20): Promise<MessageContact[]> {
  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  params.set('limit', String(limit));
  const res = await call(() =>
    apiGet<{ contacts?: Array<{ type: 'guest'; id: string | number; name: string; messageable: boolean }> }>(
      planningPath(`/inbox/contacts?${params.toString()}`),
    ),
  );
  return (res?.contacts ?? []).map(c => ({
    type: 'guest' as const, id: String(c.id), name: c.name ?? '', messageable: c.messageable === true,
  }));
}

/**
 * Sends and stores a WhatsApp message. Pass the same `idempotencyKey` when
 * retrying the same user action so the server does not send it twice. A
 * provider rejection (502) stores the failed message, so it shows in the
 * thread with its delivery error, and then throws.
 */
export async function sendMessage(input: SendMessageInput, idempotencyKey?: string): Promise<InboxMessage> {
  const body: Record<string, unknown> = { text: input.text };
  if (input.contactType) body.contactType = input.contactType;
  if (input.contactId) body.contactId = input.contactId;
  if (input.to) body.to = input.to;
  if (input.jid) body.jid = input.jid;
  try {
    const row = await apiPost<Raw>(
      planningPath('/inbox/send'),
      body,
      idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    );
    const msg = toInboxMessage(row);
    setCache({ messages: upsert(getCommunicationsMessages(), msg), mutedThreads: getMutedThreads() });
    return msg;
  } catch (err) {
    const mapped = mapCommunicationsError(err);
    if (mapped.failedMessage?.id) {
      setCache({ messages: upsert(getCommunicationsMessages(), mapped.failedMessage), mutedThreads: getMutedThreads() });
    }
    throw mapped;
  }
}

/** Saves a journal note or draft. Nothing is delivered. */
export async function createNote(input: NoteInput): Promise<InboxMessage> {
  const row = await call(() => apiPost<Raw>(planningPath('/inbox'), { type: 'note', ...input }));
  const msg = toInboxMessage(row);
  setCache({ messages: upsert(getCommunicationsMessages(), msg), mutedThreads: getMutedThreads() });
  return msg;
}

export async function markMessageRead(id: string): Promise<void> {
  await call(() => apiPut<Raw>(planningPath(`/inbox/${id}`), { read: true }));
  setCache({ messages: getCommunicationsMessages().map(m => (m.id === id ? { ...m, read: true } : m)), mutedThreads: getMutedThreads() });
}

export async function markThreadRead(threadKey: string): Promise<void> {
  await call(() => apiPost<{ updated: number }>(planningPath('/inbox/threads/read'), { thread_key: threadKey }));
  setCache({
    messages: getCommunicationsMessages().map(m => (m.thread_key === threadKey ? { ...m, read: true } : m)),
    mutedThreads: getMutedThreads(),
  });
}

async function setMuted(path: '/inbox/threads/mute' | '/inbox/threads/unmute', threadKey: string): Promise<void> {
  const res = await call(() => apiPost<{ muted_threads?: string[] }>(planningPath(path), { thread_key: threadKey }));
  const muted = Array.isArray(res?.muted_threads) ? res.muted_threads : [];
  setCache({
    messages: getCommunicationsMessages().map(m => (m.thread_key ? { ...m, muted: muted.includes(m.thread_key) } : m)),
    mutedThreads: muted,
  });
}

export const muteThread = (threadKey: string) => setMuted('/inbox/threads/mute', threadKey);
export const unmuteThread = (threadKey: string) => setMuted('/inbox/threads/unmute', threadKey);

export async function deleteMessage(id: string): Promise<void> {
  await call(() => apiDelete<{ message?: string }>(planningPath(`/inbox/${id}`)));
  setCache({ messages: getCommunicationsMessages().filter(m => m.id !== id), mutedThreads: getMutedThreads() });
}

// ── provider keys (admin) ───────────────────────────────────────────────────

type RawKeySummary = { provider?: string; configured?: boolean; masked?: string | null; updated_at?: string | null };

const toKeySummary = (r: RawKeySummary | undefined): MessagingKeySummary => ({
  provider: r?.provider ?? 'gupshup',
  configured: r?.configured !== false,
  masked: r?.masked ?? null,
  updatedAt: r?.updated_at ?? null,
});

/** Saves the Gupshup details. The response is only a masked summary. */
export async function saveGupshupKeys(input: GupshupKeysInput): Promise<MessagingKeySummary> {
  const res = await call(() => apiPut<RawKeySummary>(planningPath('/messaging-keys/gupshup'), input));
  return toKeySummary(res);
}

export async function removeGupshupKeys(): Promise<void> {
  await call(() => apiDelete(planningPath('/messaging-keys/gupshup')));
}
